import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../store';
import { updateError } from './user';
import { callApi } from '../../utils/apiCall';

// ─────────────────────────────────────────────────────────────────────────────
// formDeliveries slice
// ─────────────────────────────────────────────────────────────────────────────
// Parallel to `consentV2` — identical state shape, identical reducers, identical
// thunk surface. The only behavioural difference is that the consent-request
// endpoints (`/consent-requests/`) are addressed via the new
// `/form-deliveries/` URL prefix, which the backend mounts on the same
// viewsets (Track A.3 of the form-deliveries-and-reports plan, 2026-04-26).
//
// Components should be migrated 1:1 from `consentV2` → `formDeliveries` over
// time. Until a consumer is migrated, both slices coexist and the wire-level
// contract is identical.
// ─────────────────────────────────────────────────────────────────────────────

const initialState = {
    isLoading: false,
    formsLoading: false,
    error: null,
    forms: { results: [], count: 0 },
    currentForm: null,
    submissions: { results: [], count: 0 },
    currentSubmission: null,
    consentRequests: { results: [], count: 0 },
    leadConsentRequests: { results: [], count: 0 },
    leadConsentLoading: false,
    // Archived form-deliveries for a single lead. Kept separate from
    // `leadConsentRequests` so the active list isn't polluted by archived
    // rows when the UI opens the archived section on demand.
    leadArchivedRequests: { results: [], count: 0 },
    leadArchivedLoading: false,
    // Flips true after the first successful (or empty-success) fetch — lets
    // the UI distinguish "user has opened archived at least once" from
    // "never fetched". Only network/HTTP failure leaves this false.
    leadArchivedLoaded: false,
    // Submissions scoped to a single lead (for LeadFormsTab)
    leadSubmissions: { results: [], count: 0 },
    leadSubmissionsLoading: false,
    formTypes: [],
    formTypesLoading: false,
    // Auto-send trigger rules (stored as practice choices)
    formTriggers: [],
    formTriggersLoading: false,
    // Practice-level form branding config (stored as a practice choice)
    formBranding: null,
    formBrandingLoading: false,
    // Treatment plan templates
    treatmentPlanTemplates: { results: [], count: 0 },
    treatmentPlanTemplatesLoading: false,
    // Per-lead treatment plans (for LeadFormsTab)
    leadTreatmentPlans: { results: [], count: 0 },
    leadTreatmentPlansLoading: false,
};

const slice = createSlice({
    name: 'formDeliveries',
    initialState,
    reducers: {
        startLoading(state) {
            state.isLoading = true;
        },
        stopLoading(state) {
            state.isLoading = false;
        },
        hasError(state, action) {
            state.isLoading = false;
            state.formsLoading = false;
            state.error = action.payload;
        },
        startFormsLoading(state) {
            state.formsLoading = true;
        },
        setForms(state, action) {
            state.isLoading = false;
            state.formsLoading = false;
            state.forms = action.payload;
        },
        setCurrentForm(state, action) {
            state.isLoading = false;
            state.currentForm = action.payload;
        },
        setSubmissions(state, action) {
            state.isLoading = false;
            state.submissions = action.payload;
        },
        setCurrentSubmission(state, action) {
            state.isLoading = false;
            state.currentSubmission = action.payload;
        },
        resetCurrentForm(state) {
            state.currentForm = null;
        },
        setConsentRequests(state, action) {
            state.isLoading = false;
            state.consentRequests = action.payload;
        },
        setLeadConsentRequests(state, action) {
            state.leadConsentLoading = false;
            state.leadConsentRequests = action.payload;
        },
        startLeadConsentLoading(state) {
            state.leadConsentLoading = true;
        },
        clearLeadConsentRequests(state) {
            state.leadConsentRequests = { results: [], count: 0 };
            state.leadConsentLoading = false;
        },
        startLeadArchivedLoading(state) {
            state.leadArchivedLoading = true;
        },
        setLeadArchivedRequests(state, action) {
            state.leadArchivedLoading = false;
            state.leadArchivedLoaded = true;
            state.leadArchivedRequests = action.payload;
        },
        // Used on network/HTTP failure: stops the spinner WITHOUT flipping
        // `leadArchivedLoaded` to true, so the UI can tell "never successfully
        // fetched" apart from "fetched and got zero rows".
        stopLeadArchivedLoading(state) {
            state.leadArchivedLoading = false;
        },
        clearLeadArchivedRequests(state) {
            state.leadArchivedRequests = { results: [], count: 0 };
            state.leadArchivedLoading = false;
            state.leadArchivedLoaded = false;
        },
        setLeadSubmissions(state, action) {
            state.leadSubmissionsLoading = false;
            // Fix #6: support append semantics for "Load more" pagination. When
            // payload.append is true we concat the new page to the existing
            // results instead of replacing them; otherwise behaviour is
            // unchanged for callers that send a full list.
            //
            // Code-review fix: when append=true AND the new page is empty
            // (typically an append failure path), preserve the existing
            // ``count`` rather than overwriting it with 0 — otherwise a
            // single failed Load More would erase the count and hide the
            // button permanently.
            const { results = [], count = 0, append = false } = action.payload || {};
            if (append) {
                const existing = state.leadSubmissions.results || [];
                state.leadSubmissions = {
                    results: [...existing, ...results],
                    count: results.length === 0
                        ? (state.leadSubmissions.count || 0)
                        : count,
                };
            } else {
                state.leadSubmissions = { results, count };
            }
        },
        startLeadSubmissionsLoading(state) {
            state.leadSubmissionsLoading = true;
        },
        clearLeadSubmissions(state) {
            state.leadSubmissions = { results: [], count: 0 };
            state.leadSubmissionsLoading = false;
        },
        startFormTypesLoading(state) {
            state.formTypesLoading = true;
        },
        setFormTypes(state, action) {
            state.formTypes = action.payload;
            state.formTypesLoading = false;
        },
        stopFormTypesLoading(state) {
            state.formTypesLoading = false;
        },
        setFormTriggers(state, action) {
            state.formTriggers = action.payload;
            state.formTriggersLoading = false;
        },
        startFormTriggersLoading(state) {
            state.formTriggersLoading = true;
        },
        setFormBranding(state, action) {
            state.formBranding = action.payload;
            state.formBrandingLoading = false;
        },
        startFormBrandingLoading(state) {
            state.formBrandingLoading = true;
        },
        setTreatmentPlanTemplates(state, action) {
            state.treatmentPlanTemplates = action.payload;
            state.treatmentPlanTemplatesLoading = false;
        },
        startTreatmentPlanTemplatesLoading(state) {
            state.treatmentPlanTemplatesLoading = true;
        },
        stopTreatmentPlanTemplatesLoading(state) {
            state.treatmentPlanTemplatesLoading = false;
        },
        setLeadTreatmentPlans(state, action) {
            state.leadTreatmentPlans = action.payload;
            state.leadTreatmentPlansLoading = false;
        },
        startLeadTreatmentPlansLoading(state) {
            state.leadTreatmentPlansLoading = true;
        },
        clearLeadTreatmentPlans(state) {
            state.leadTreatmentPlans = { results: [], count: 0 };
            state.leadTreatmentPlansLoading = false;
        },
    },
});

export default slice.reducer;

export const {
    resetCurrentForm,
    clearLeadConsentRequests,
    clearLeadArchivedRequests,
    clearLeadSubmissions,
    clearLeadTreatmentPlans,
    setFormTypes,
} = slice.actions;

export function getConsentForms(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            dispatch(slice.actions.startFormsLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.formType) searchParams.set('form_type', params.formType);
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setForms(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getConsentFormDetail(practiceId, formId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentForm(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function createConsentForm(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            // Backend now supports top-level enable_memory, form_mode, step_groups, version fields.
            const payload = { ...data, form_type: data.form_type || 'CONSENT' };
            if (data.enable_memory !== undefined) payload.enable_memory = data.enable_memory;
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/`,
                'post',
                payload
            );
            if (response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function updateConsentForm(practiceId, formId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            // Backend now supports top-level enable_memory, form_mode, step_groups, version fields.
            const payload = { ...data, form_type: data.form_type || 'CONSENT' };
            if (data.enable_memory !== undefined) payload.enable_memory = data.enable_memory;
            if (data.form_mode) payload.form_mode = data.form_mode;
            if (data.step_groups) payload.step_groups = data.step_groups;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'patch',
                payload
            );
            if (response.status === 200) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function deleteConsentForm(practiceId, formId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/`,
                'delete'
            );
            if (response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

export function getConsentSubmissions(practiceId, formId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setSubmissions(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function getConsentSubmissionDetail(practiceId, formId, submissionId) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/forms/${formId}/submissions/${submissionId}/`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentSubmission(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function getConsentRequests(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.status) searchParams.set('status', params.status);
            if (params.form) searchParams.set('form', params.form);
            if (params.lead) searchParams.set('lead', params.lead);
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setConsentRequests(response.data));
            } else {
                dispatch(slice.actions.hasError(response.data));
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
        }
    };
}

export function revokeConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/${requestId}/revoke/`,
                'post'
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Fetch all form deliveries (formerly "consent requests") scoped to a single
 * lead. The lead-nested URL `/leads/{leadId}/consent-requests/` was the legacy
 * route; the canonical endpoint is the practice-level `/form-deliveries/` list
 * with a `lead` filter (the viewset declares `lead` in `filterset_fields`, so
 * this is the supported way to scope to a single patient on the new prefix).
 *
 * Track A.3, form-deliveries-and-reports plan (2026-04-26): every other thunk
 * in this slice already migrated to `/form-deliveries/`; this one was left
 * pointing at the deprecated nested route, which meant Lead Detail's Forms tab
 * silently kept hitting the old URL.
 */
export function getLeadConsentRequests(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadConsentLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('lead', String(leadId));
            searchParams.set('ordering', '-created_at');
            // The list endpoint is paginated; ask for a generous page so a
            // patient with many sequenced deliveries doesn't get truncated.
            searchParams.set('limit', '100');
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadConsentRequests({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadConsentRequests({ results: [], count: 0 }));
            }
        } catch (error) {
            dispatch(slice.actions.setLeadConsentRequests({ results: [], count: 0 }));
        }
    };
}

/**
 * Fetch ARCHIVED form deliveries scoped to a single lead. Mirrors
 * `getLeadConsentRequests` but appends `archived=true` so the backend returns
 * only archived rows (active rows live in `leadConsentRequests`; the two
 * slices intentionally don't share state).
 *
 * The accepted values for the backend's `archived` query param are
 * "true" / "false" / "all"; we hardcode "true" here. On HTTP/network failure
 * we set an empty payload but `leadArchivedLoaded` stays false so the UI can
 * retry; on a 200 with zero results it flips true (empty success).
 */
export function getLeadArchivedRequests(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadArchivedLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('lead', String(leadId));
            searchParams.set('ordering', '-created_at');
            searchParams.set('archived', 'true');
            // The list endpoint is paginated; ask for a generous page so a
            // patient with many archived deliveries doesn't get truncated.
            searchParams.set('limit', '100');
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadArchivedRequests({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                // HTTP failure — leave `leadArchivedLoaded` false by NOT
                // calling the setter (which would flip it true). Just stop
                // the loading spinner so the UI can retry.
                dispatch(slice.actions.stopLeadArchivedLoading());
            }
        } catch (error) {
            // Network failure — same as above: don't flip `leadArchivedLoaded`.
            dispatch(slice.actions.stopLeadArchivedLoading());
        }
    };
}

/**
 * Fetch custom form type configurations from the practice choices API.
 * Custom form types are stored as choices with metadata.type = 'form_type_config'.
 * Falls back to an empty array on error — callers always merge with SYSTEM_FORM_TYPES.
 */
export function getFormTypes(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormTypesLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_type_config`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormTypes(results));
            } else {
                dispatch(slice.actions.setFormTypes([]));
            }
        } catch {
            dispatch(slice.actions.stopFormTypesLoading());
        }
    };
}

/**
 * Create a custom form type choice.
 * @param {string} practiceId
 * @param {{ value: string, label: string, color: string, icon: string }} typeData
 * @param {Function} callback - called with { success, data }
 */
export function saveCustomFormType(practiceId, typeData, callback) {
    return async () => {
        try {
            const payload = {
                name: typeData.label,
                value: typeData.value,
                metadata: {
                    type: 'form_type_config',
                    color: typeData.color || '#637381',
                    icon: typeData.icon || 'solar:document-bold-duotone',
                },
            };
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/`,
                'post',
                payload
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormTypes(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save form type' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save form type' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a custom form type choice.
 * @param {string} practiceId
 * @param {string|number} choiceId  - the choice record's backend ID
 * @param {Function} callback
 */
export function deleteCustomFormType(practiceId, choiceId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/${choiceId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getFormTypes(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete form type' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete form type' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Fetch form submissions for a specific lead.
 * Endpoint: GET /api/v1/practices/{practiceId}/leads/{leadId}/form-submissions/
 *
 * Fix #6: accepts an optional `params` object with `limit` and `offset` for
 * pagination. Default page size is 20 — submissions can be 50+ rows with heavy
 * `fields[]` and `signature_data` payloads, so we no longer pull the whole
 * list at mount time. Pass `append: true` to concat the page onto the existing
 * results (used by `loadMoreLeadFormSubmissions`).
 */
export function getLeadFormSubmissions(practiceId, leadId, params = {}) {
    const { limit = 20, offset = 0, append = false } = params;
    return async () => {
        try {
            dispatch(slice.actions.startLeadSubmissionsLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/form-submissions/?ordering=-submitted_at&limit=${limit}&offset=${offset}`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadSubmissions({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                    append,
                }));
            } else if (!append) {
                dispatch(slice.actions.setLeadSubmissions({ results: [], count: 0 }));
            } else {
                // On append failure, leave existing results intact but stop loading.
                dispatch(slice.actions.setLeadSubmissions({
                    results: [],
                    count: 0,
                    append: true,
                }));
            }
        } catch (error) {
            if (!append) {
                dispatch(slice.actions.setLeadSubmissions({ results: [], count: 0 }));
            } else {
                dispatch(slice.actions.setLeadSubmissions({
                    results: [],
                    count: 0,
                    append: true,
                }));
            }
        }
    };
}

/**
 * Fetch the next page of submissions and APPEND them onto the existing list.
 * Thin wrapper around `getLeadFormSubmissions` that forces `append: true`, used
 * by the LeadFormsTab "Load more" button.
 */
export function loadMoreLeadFormSubmissions(practiceId, leadId, offset, limit = 20) {
    return getLeadFormSubmissions(practiceId, leadId, { limit, offset, append: true });
}

/**
 * Resend a consent request that has expired.
 * Endpoint: POST /api/v1/practices/{practiceId}/form-deliveries/{requestId}/resend/
 */
/** Same-link reminder; never creates or revokes a request. */
export function remindFormDelivery(practiceId, requestId, channel, idempotencyKey, callback) {
    return async () => {
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/form-deliveries/${requestId}/remind/`, 'post', { channel, idempotency_key: idempotencyKey });
            callback?.({ success: [200, 202].includes(response.status), data: response.data, status: response.status });
        } catch {
            callback?.({ success: false });
        }
    };
}

export function resendConsentRequest(practiceId, requestId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/${requestId}/resend/`,
                'post'
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Something went wrong' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─────────────────────────────────────────────────────────────────────────────
// Track B.2 — kebab-action thunks for the Submissions page
// ─────────────────────────────────────────────────────────────────────────────
// Thin name-aliases for the resend/revoke endpoints that follow the
// "form delivery" terminology from the plan doc. They share the underlying
// HTTP contract with `resendConsentRequest` / `revokeConsentRequest` above —
// kept distinct so the Submissions page reads like a workflow ("resend the
// delivery") rather than the legacy "consent request" naming.
//
// Backend contract (B.1.4 / B.1.5):
//   - POST /form-deliveries/{id}/resend/  → 201 with {old_consent_request, new_consent_request}.
//                                            400 on already-REVOKED or SUBMITTED.
//   - POST /form-deliveries/{id}/revoke/  → 200 with revoked CR.
//                                            400 on already-REVOKED (NOT idempotent) or SUBMITTED.
// On 400 the backend's error body is surfaced via the callback and the
// caller is expected to render it via toast.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Resend a form delivery — revokes the old CR and creates a new one with the
 * same form / lead / treatments / channel. Returns the new CR on success.
 * Hits the same endpoint as `resendConsentRequest` but uses delivery-shaped
 * naming for the new Submissions page kebab.
 */
export function resendFormDelivery(practiceId, deliveryId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/${deliveryId}/resend/`,
                'post'
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                // 400 here typically means already-REVOKED or SUBMITTED — surface
                // the body to the caller without dispatching a global error so the
                // kebab can render a contextual toast.
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Revoke a form delivery — sets the CR to REVOKED. Not idempotent: the
 * backend returns 400 if the CR is already REVOKED or SUBMITTED. The kebab
 * already disables the action in those states, so a 400 indicates a
 * client-state regression worth surfacing.
 */
export function revokeFormDelivery(practiceId, deliveryId, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/${deliveryId}/revoke/`,
                'post'
            );
            if (response.status === 200 || response.status === 204) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Form Trigger Thunks ─────────────────────────────────────────────────────
// Auto-send rules are stored as practice choices with metadata.type = 'form_trigger_config'.

/**
 * Fetch all auto-send trigger rules for a practice.
 */
export function getFormTriggers(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormTriggersLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_trigger_config&ordering=created_at`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormTriggers(results));
            } else {
                dispatch(slice.actions.setFormTriggers([]));
            }
        } catch {
            dispatch(slice.actions.setFormTriggers([]));
        }
    };
}

/**
 * Create a new auto-send trigger rule.
 * @param {string} practiceId
 * @param {{ trigger_status: string, form_id: number, form_name: string, form_type: string }} triggerData
 * @param {Function} callback
 */
export function saveFormTrigger(practiceId, triggerData, callback) {
    return async () => {
        try {
            const payload = {
                name: `form_trigger_${triggerData.trigger_status}`,
                value: triggerData.trigger_status,
                metadata: {
                    type: 'form_trigger_config',
                    trigger_status: triggerData.trigger_status,
                    form_id: triggerData.form_id,
                    form_name: triggerData.form_name,
                    form_type: triggerData.form_type || 'CONSENT',
                },
            };
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/`,
                'post',
                payload
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormTriggers(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save trigger rule' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save trigger rule' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete an auto-send trigger rule by choice ID.
 */
export function deleteFormTrigger(practiceId, choiceId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/${choiceId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getFormTriggers(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete trigger rule' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete trigger rule' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Form Branding Thunks ────────────────────────────────────────────────────
// Branding config is stored as a single practice choice with metadata.type = 'form_branding_config'.

/**
 * Fetch the practice form branding configuration.
 * Returns the first result (there should only be one).
 */
export function getFormBranding(practiceId) {
    return async () => {
        try {
            dispatch(slice.actions.startFormBrandingLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/choices/?metadata__type=form_branding_config&ordering=-created_at&limit=1`
            );
            if (response.status === 200) {
                const results = response.data?.results || response.data || [];
                dispatch(slice.actions.setFormBranding(results[0] || null));
            } else {
                dispatch(slice.actions.setFormBranding(null));
            }
        } catch {
            dispatch(slice.actions.setFormBranding(null));
        }
    };
}

/**
 * Create or update the practice form branding configuration.
 * If existingId is provided, PATCHes the existing choice; otherwise POSTs a new one.
 * @param {string} practiceId
 * @param {{ logo_url: string, display_name: string, primary_color: string, contact_email: string, contact_phone: string }} data
 * @param {string|number|null} existingId  - the choice record ID to update, or null to create
 * @param {Function} callback
 */
export function saveFormBranding(practiceId, data, existingId, callback) {
    return async () => {
        try {
            const payload = {
                name: 'form_branding',
                value: 'form_branding',
                metadata: {
                    type: 'form_branding_config',
                    logo_url: data.logo_url || '',
                    display_name: data.display_name || '',
                    primary_color: data.primary_color || '#00AB55',
                    contact_email: data.contact_email || '',
                    contact_phone: data.contact_phone || '',
                },
            };
            const url = existingId
                ? `/api/v1/practices/${practiceId}/choices/${existingId}/`
                : `/api/v1/practices/${practiceId}/choices/`;
            const method = existingId ? 'patch' : 'post';
            const response = await callApi(url, method, payload);
            const expectedStatus = existingId ? 200 : 201;
            if (response.status === expectedStatus) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getFormBranding(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to save branding' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to save branding' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Treatment Plan Template Thunks ─────────────────────────────────────────

/**
 * Fetch all treatment plan templates for a practice.
 * Endpoint: GET /api/v1/practices/{practiceId}/treatment-plan-templates/
 */
export function getTreatmentPlanTemplates(practiceId, params = {}) {
    return async () => {
        try {
            dispatch(slice.actions.startTreatmentPlanTemplatesLoading());
            const searchParams = new URLSearchParams();
            searchParams.set('ordering', '-created_at');
            if (params.offset !== undefined) searchParams.set('offset', params.offset);
            if (params.limit !== undefined) searchParams.set('limit', params.limit);
            if (params.search) searchParams.set('search', params.search);
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/?${searchParams.toString()}`
            );
            if (response.status === 200) {
                dispatch(slice.actions.setTreatmentPlanTemplates(response.data));
            } else {
                dispatch(slice.actions.stopTreatmentPlanTemplatesLoading());
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to load templates' }));
            }
        } catch (error) {
            dispatch(slice.actions.stopTreatmentPlanTemplatesLoading());
            dispatch(updateError({ success: false, message: error.message || 'Failed to load templates' }));
        }
    };
}

/**
 * Create a new treatment plan template.
 * Endpoint: POST /api/v1/practices/{practiceId}/treatment-plan-templates/
 */
export function createTreatmentPlanTemplate(practiceId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/`,
                'post',
                data
            );
            if (response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create template' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to create template' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update an existing treatment plan template.
 * Endpoint: PATCH /api/v1/practices/{practiceId}/treatment-plan-templates/{templateId}/
 */
export function updateTreatmentPlanTemplate(practiceId, templateId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/${templateId}/`,
                'patch',
                data
            );
            if (response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update template' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to update template' }));
            if (callback) callback({ success: false });
        }
    };
}

// ─── Per-Lead Treatment Plan Thunks ──────────────────────────────────────────
// Endpoint: /api/v1/practices/{practiceId}/leads/{leadId}/treatment-plans/
// (Placeholder endpoints — backend implementation tracked in PROMPT_06)

/**
 * Fetch all treatment plans for a specific lead.
 */
export function getLeadTreatmentPlans(practiceId, leadId) {
    return async () => {
        try {
            dispatch(slice.actions.startLeadTreatmentPlansLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/?ordering=-created_at`
            );
            if (response.status === 200) {
                const { data } = response;
                dispatch(slice.actions.setLeadTreatmentPlans({
                    results: data.results || data || [],
                    count: data.count ?? (data.results || data || []).length,
                }));
            } else {
                dispatch(slice.actions.setLeadTreatmentPlans({ results: [], count: 0 }));
            }
        } catch {
            dispatch(slice.actions.setLeadTreatmentPlans({ results: [], count: 0 }));
        }
    };
}

/**
 * Create a new treatment plan for a lead.
 * Stores practice-filled fields, options, cost items, and patient info.
 * @param {string} practiceId
 * @param {string|number} leadId
 * @param {object} data - { options: [], patient_name, treatment_name, form_id, expires_in_hours }
 * @param {Function} callback - called with { success, data: { access_token, url, ... } }
 */
export function createLeadTreatmentPlan(practiceId, leadId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/`,
                'post',
                data
            );
            if (response.status === 201 || response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to create treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Update an existing treatment plan (e.g., after editing).
 */
export function updateLeadTreatmentPlan(practiceId, leadId, planId, data, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/${planId}/`,
                'patch',
                data
            );
            if (response.status === 200) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to update treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to update treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Send an existing treatment plan via a channel (EMAIL / WHATSAPP / SMS).
 */
export function sendLeadTreatmentPlan(practiceId, leadId, planId, channel, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/treatment-plans/${planId}/send/`,
                'post',
                { channel }
            );
            if (response.status === 200 || response.status === 201) {
                if (callback) callback({ success: true, data: response.data });
                dispatch(getLeadTreatmentPlans(practiceId, leadId));
            } else {
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to send treatment plan' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to send treatment plan' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Delete a treatment plan template.
 * Endpoint: DELETE /api/v1/practices/{practiceId}/treatment-plan-templates/{templateId}/
 */
export function deleteTreatmentPlanTemplate(practiceId, templateId, callback) {
    return async () => {
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/treatment-plan-templates/${templateId}/`,
                'delete'
            );
            if (response.status === 204) {
                if (callback) callback({ success: true });
                dispatch(getTreatmentPlanTemplates(practiceId));
            } else {
                dispatch(updateError({ success: false, message: 'Failed to delete template' }));
                if (callback) callback({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Failed to delete template' }));
            if (callback) callback({ success: false });
        }
    };
}

/**
 * Create a consent request using the new API fields.
 * Supports: treatment_ids, sequence_id, sequence_order, channel.
 * Endpoint: POST /api/v1/practices/{practiceId}/form-deliveries/
 */
export function createConsentRequest(practiceId, data, callback) {
    return async () => {
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(
                `/api/v1/practices/${practiceId}/form-deliveries/`,
                'post',
                data
            );
            if (response.status === 200 || response.status === 201) {
                dispatch(slice.actions.stopLoading());
                if (callback) callback({ success: true, data: response.data });
            } else {
                dispatch(slice.actions.hasError(response.data));
                dispatch(updateError({ success: false, message: JSON.stringify(response.data) || 'Failed to create consent request' }));
                if (callback) callback({ success: false, data: response.data });
            }
        } catch (error) {
            dispatch(slice.actions.hasError(error.message));
            dispatch(updateError({ success: false, message: error.message || 'Failed to create consent request' }));
            if (callback) callback({ success: false });
        }
    };
}
